import {
  CaseDataType,
  ENTITY_TYPE,
  flattenEntityRelationshipTree,
  getLayoutByEntityNameAndTypeApi,
  getLayoutByIdApi,
  getLayoutTree,
  hideLoader,
  LayoutBuilderTabInfoTabsConfigType,
  LayoutResponseType,
  LayoutRowType,
  showLoader,
  updateWorkflowPatientData,
} from '@app-store'
import { MutableRefObject, useEffect, useMemo, useState } from 'react'
import RowRenderTabs from './RowRenderTabs'
import {
  ENTITY_NAME,
  FIELD_VALUE_CONSTANTS,
  LAYOUT_TYPE_NAMESPACE,
  NAMESPACES,
  PERMISSION_ACCESSIBILITY,
  PERMISSION_ASSIGNMENT,
  PERMISSION_MODULES,
  ROW_TYPES,
  VIEW_360_CONSTANTS,
} from '../../utils/constants'
import {
  getEntityData,
  getEntityDataByIdEmbedded,
} from '../../apis/entityData.api'
import MultiLookupRenderTabs from './MultiLookupRenderTabs'
import Multilookup360Wrapper from '../view-360/Multilookup360Wrapper'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { v4 as uuid } from 'uuid'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import { Accordion, Modal, MODE_CONSTANTS } from '@ui-kit'
import ThreadsNotesAccordion from '../ThreadsNotesAccordion'
import { Row } from '../../types/layout.types'
import { RowTabIndex } from '../../types/common'

import { isSuperAdmin } from '../../utils/permission'
import { fetchCaseData, permissionObject } from '../../utils/helperFunctions'
import { noop, isEmpty, cloneDeep } from 'lodash'
import TabSubFormRow from './TabSubFormRow'
import { LayoutRenderTabsProvider } from './LayoutRenderTabsContext'
import {
  hasHybridLayoutFormRules,
  HybridFormBehaviorScope,
  triggerHybridFormBehaviorHydrate,
} from '../../form-behavior/HybridFormBehaviorScope'
import { isRowHiddenByFormBehavior } from '../../form-behavior/formBehavior.utils'
import { useFormBehaviorState } from '../../form-behavior/useFormBehaviorState'
import { formatDataBeforeLoad } from '../../utils/form.utils'

const LayoutRenderTabs = ({
  row,
  meta,
  layoutList,
  parentLayout,
  recordId,
  entityId,
  tabIndexMap,
  isPrintLetterView,
  isWorkflow,
}: {
  row: LayoutRowType
  meta: LayoutBuilderTabInfoTabsConfigType
  layoutList: MutableRefObject<{ [key: string]: LayoutResponseType }>
  parentLayout: LayoutResponseType
  recordId: string
  entityId: string
  tabIndexMap?: RowTabIndex
  isPrintLetterView?: boolean
  isWorkflow?: boolean
}) => {
  const rowMeta = row?.lookup_meta?.is_custom
    ? parentLayout?.layout_data?.meta?.custom_tab_layouts?.[row?.rowId ?? '']
    : meta?.[row?.lookup_meta?.lookup_keyName ?? '']
  const dispatch = useAppDispatch()
  const [viewLayout, setViewLayout] = useState<LayoutResponseType | null>(null)
  const [editLayout, setEditLayout] = useState<LayoutResponseType | null>(null)
  const [entityData, setEntityData] = useState<FieldValues>({})
  const [viewEntityRecordId, setViewEntityRecordId] = useState<string | null>(
    null
  )
  const [hasViewPermission, setHasViewPermission] = useState(true)
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions.programPermissions
  )
  const loaderVisible = useAppSelector(
    (state: RootState) => state?.loader.loading
  )
  const allEntities = useAppSelector(
    (state: RootState) => state.entity.entitiesByName
  )
  const id = recordId

  const behaviorMethods = useForm({ reValidateMode: 'onBlur' })
  const { setValue, getValues, reset } = behaviorMethods
  const formUniqueId = useMemo(() => `tabs-view-${uuid()}`, [])
  const formBehavior = useFormBehaviorState(viewLayout?.uid)

  useEffect(() => {
    if (!viewLayout || isEmpty(entityData)) return
    const dataForBehavior = cloneDeep(entityData)
    formatDataBeforeLoad(
      dataForBehavior,
      viewLayout?.layout_data?.added_fields ?? {}
    )
    reset(dataForBehavior)
    if (hasHybridLayoutFormRules(viewLayout)) {
      triggerHybridFormBehaviorHydrate(viewLayout.uid)
    }
  }, [entityData, reset, viewLayout])

  const isLayoutRowHidden = (layoutRow: LayoutRowType) =>
    isRowHiddenByFormBehavior(layoutRow?.rowKey, formBehavior.hiddenOverrides)

  const fetchRelationshipTree = async ({ entityId }: { entityId: string }) => {
    try {
      const response = await getLayoutTree(
        entityId || '',
        FIELD_VALUE_CONSTANTS?.ONE?.toString()
      )
      if (response?.data) {
        const flattenTree = flattenEntityRelationshipTree(response?.data)
        return flattenTree
      }
    } catch (error) {
      console.log(error)
    }
  }

  const checkCasePermission = async (caseDataResponse: {
    data?: CaseDataType
  }) => {
    try {
      if (caseDataResponse.data) {
        const permissionValue = permissionObject.Queues_And_Cases_View(
          programPermissions,
          PERMISSION_MODULES.QUEUE_AND_CASE_VISIBILITY,
          PERMISSION_ACCESSIBILITY.VIEW,
          caseDataResponse.data.programs?.id?.toString() ?? ''
        )
        setHasViewPermission(permissionValue)
      }
    } catch (err) {
      setHasViewPermission(false)
      console.error(err)
    }
  }

  const fetchEntityData = async (layout: LayoutResponseType) => {
    try {
      const caseDataResponse = await fetchCaseData(id || '', dispatch)
      if (isSuperAdmin()) {
        setHasViewPermission(true)
      } else if (
        (layout?.entity_name === 'case' || layout?.entity_name === 'cases') &&
        caseDataResponse
      ) {
        await checkCasePermission(caseDataResponse)
      }
      const flattenTree = await fetchRelationshipTree({ entityId: entityId })
      if (
        row?.lookup_meta?.mapped_as === VIEW_360_CONSTANTS?.MANYTOONE ||
        row?.lookup_meta?.mapped_as === VIEW_360_CONSTANTS?.ONETOONE
      ) {
        let lookupKeyname = ''
        flattenTree?.forEach((relation) => {
          if (relation.field_name === row?.lookup_meta?.lookup_keyName) {
            lookupKeyname = relation.field_name?.toString() ?? ''
          }
        })
        const entityResponse = await getEntityDataByIdEmbedded(
          parentLayout?.entity_name,
          Number(entityId),
          [lookupKeyname, 'record_id'],
          Number(id)
        )
        if (
          layout?.entity?.entity_name?.toLowerCase() === ENTITY_NAME.PATIENTS &&
          entityResponse.data.patient?.patient_key
        ) {
          dispatch(
            updateWorkflowPatientData({
              id: entityResponse.data.patient?.patient_key,
            })
          )
        }
        setViewEntityRecordId(entityResponse?.data?.requestor?.record_id)
        const lookupDataId = entityResponse?.data?.[lookupKeyname]?.id ?? ''
        if (lookupDataId) {
          const response = await getEntityData(
            layout?.entity_name || '',
            Number(layout?.entity_id),
            Number(layout?.id),
            Number(lookupDataId)
          )
          if (response) {
            setEntityData(response?.data)
            if (response?.data?.entity_type) {
              const layout = await getLayoutByEntityNameAndTypeApi({
                entityName: response?.data?.entity_type,
                type: LAYOUT_TYPE_NAMESPACE?.VIEW_EDIT_TYPE,
                isDefault: true,
              })
              setEditLayout(layout?.data?.[0])
            }
          }
        }
      } else {
        const response = await getEntityData(
          layout?.entity_name || '',
          Number(layout?.entity_id),
          Number(layout?.id),
          Number(id)
        )
        if (response) {
          setEntityData(response?.data)
        }
      }
    } catch (err) {
      console.error(err)
    }
  }

  const fetchLayouts = async () => {
    try {
      if (
        (rowMeta?.list_view_layout_id &&
          !(rowMeta?.list_view_layout_id in layoutList.current)) ||
        (rowMeta?.create_layout_id &&
          !(rowMeta?.create_layout_id in layoutList.current))
      ) {
        const view = await getLayoutByIdApi({
          entityId: row?.lookup_meta?.id ?? '',
          layoutId:
            rowMeta?.list_view_layout_id || rowMeta?.create_layout_id || '',
        })

        if (
          rowMeta?.create_layout_id &&
          !(rowMeta?.create_layout_id in layoutList)
        ) {
          const edit = await getLayoutByIdApi({
            entityId: row?.lookup_meta?.id ?? '',
            layoutId: rowMeta?.create_layout_id,
          })
          setEditLayout(edit?.data)
          if (!(edit?.data?.id in layoutList.current)) {
            layoutList.current[edit?.data?.id] = edit?.data
          }
        } else {
          setEditLayout(layoutList.current[rowMeta?.create_layout_id ?? ''])
        }
        layoutList.current[view?.data?.id] = view?.data
        setViewLayout(view?.data)
        fetchEntityData(view?.data)
      } else {
        setViewLayout(layoutList.current[rowMeta?.list_view_layout_id ?? ''])
        setEditLayout(layoutList.current[rowMeta?.create_layout_id ?? ''])
        fetchEntityData(layoutList.current[rowMeta?.list_view_layout_id ?? ''])
      }
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    const loadAllData = async () => {
      dispatch(showLoader())
      try {
        if (!isSuperAdmin() && Object.keys(programPermissions).length === 0)
          return
        await fetchLayouts()
      } catch (err) {
        console.error(err)
      } finally {
        dispatch(hideLoader())
      }
    }
    loadAllData()
  }, [row, meta, programPermissions])

  if (row.rowType === ROW_TYPES.SINGLE_LOOKUP_LISTING) {
    return null
  }

  return (
    <LayoutRenderTabsProvider>
      <div>
        {(() => {
          const mappedAs = row?.lookup_meta?.mapped_as

          switch (mappedAs) {
            case VIEW_360_CONSTANTS.ROOT:
            case VIEW_360_CONSTANTS.MANYTOONE:
            case VIEW_360_CONSTANTS.ONETOONE:
              return (
                <FormProvider {...behaviorMethods}>
                  <div id={formUniqueId} />
                  <HybridFormBehaviorScope
                    layout={viewLayout}
                    formUniqueId={formUniqueId}
                    setValue={setValue}
                    getValues={getValues}
                    reset={reset}
                    mode={MODE_CONSTANTS.VIEW}
                  >
                    <>
                      {!hasViewPermission &&
                      !loaderVisible &&
                      !isPrintLetterView ? (
                        <Modal
                          isOpen={true}
                          closeModal={noop}
                          showCloseButton={false}
                          width="min-w-[40rem] max-w-[98%]"
                          cssClassName="backdrop-blur-sm"
                        >
                          <div className="flex justify-center items-center p-5">
                            {PERMISSION_ASSIGNMENT.NO_ACCESS_FOR_CASE}
                          </div>
                        </Modal>
                      ) : null}
                      {viewLayout?.layout_data?.pages?.[0]?.rows?.map(
                        (layoutRow, index) => {
                          if (isLayoutRowHidden(layoutRow)) return null
                          return (
                            <div
                              className="my-2"
                              key={layoutRow?.rowKey || layoutRow?.rowId}
                            >
                              {(() => {
                                switch (layoutRow?.rowType) {
                                  case ROW_TYPES?.MULTI_LOOKUP:
                                    return (
                                      <div
                                        id={
                                          layoutRow?.rowKey || layoutRow?.rowId
                                        }
                                      >
                                        {!isEmpty(entityData) ? (
                                          <Multilookup360Wrapper
                                            heading={
                                              layoutRow?.label ??
                                              viewLayout?.name ??
                                              ''
                                            }
                                            isViewEntity={
                                              allEntities[
                                                viewLayout?.layout_data
                                                  ?.added_fields?.[
                                                  layoutRow?.lookup_meta
                                                    ?.lookup_keyName ?? ''
                                                ]?.relationship
                                                  ?.target_entity ?? ''
                                              ]?.type?.toUpperCase() ===
                                              ENTITY_TYPE.VIEW
                                            }
                                            entityId={
                                              viewLayout?.layout_data?.added_fields?.[
                                                layoutRow?.lookup_meta
                                                  ?.lookup_keyName ?? ''
                                              ]?.d_entity_id?.toString() ?? ''
                                            }
                                            listViewId={
                                              viewLayout?.layout_data
                                                ?.added_fields?.[
                                                layoutRow?.lookup_meta
                                                  ?.lookup_keyName ?? ''
                                              ]?.list_view_layout_id ?? ''
                                            }
                                            editViewId={
                                              viewLayout?.layout_data
                                                ?.added_fields?.[
                                                layoutRow?.lookup_meta
                                                  ?.lookup_keyName ?? ''
                                              ]?.create_layout_id ?? ''
                                            }
                                            relationshipIdentifier={
                                              viewLayout?.layout_data
                                                ?.added_fields?.[
                                                layoutRow?.lookup_meta
                                                  ?.lookup_keyName ?? ''
                                              ]?.relationship
                                                ?.relation_identifier ?? ''
                                            }
                                            lookupKeyname={
                                              layoutRow?.lookup_meta
                                                ?.lookup_keyName ?? ''
                                            }
                                            parentEditLayoutId={
                                              editLayout?.id?.toString() ?? ''
                                            }
                                            recordId={
                                              entityData?.id ?? recordId
                                            }
                                            entityIdParent={entityId}
                                            isAssign={
                                              layoutRow?.lookup_meta
                                                ?.is_assign ?? false
                                            }
                                            disableFilters={
                                              !!row?.lookup_meta
                                                ?.disable_filters ||
                                              !!layoutRow?.lookup_meta
                                                ?.disable_filters
                                            }
                                            tabIndexMap={tabIndexMap}
                                          />
                                        ) : (
                                          <Accordion
                                            heading={
                                              <div className="flex justify-between items-center">
                                                <span className="font-primary">
                                                  {layoutRow?.label ??
                                                    viewLayout?.name ??
                                                    ''}
                                                </span>
                                              </div>
                                            }
                                            tabIndex={
                                              tabIndexMap?.tabIndexOffset
                                            }
                                          >
                                            <div className="flex justify-center items-center h-full m-auto font-primary text-center  min-h-20">
                                              <span>
                                                {
                                                  NAMESPACES.EMPTY_DATA_MULTILOOKUP
                                                }
                                              </span>
                                            </div>
                                          </Accordion>
                                        )}
                                      </div>
                                    )

                                  case ROW_TYPES.SINGLE_LOOKUP_LISTING:
                                    return null

                                  case ROW_TYPES.NOTES:
                                  case ROW_TYPES.NOTE:
                                    return (
                                      <ThreadsNotesAccordion
                                        relationIdentifier={
                                          layoutRow?.relation_identifier ?? ''
                                        }
                                        row={layoutRow as Row}
                                        id={id || ''}
                                        mode={MODE_CONSTANTS.VIEW}
                                        createLayoutId={
                                          viewLayout?.layout_data
                                            ?.added_fields?.[
                                            layoutRow?.lookup_meta
                                              ?.lookup_keyName ?? ''
                                          ]?.create_layout_id ?? ''
                                        }
                                        entityName={
                                          viewLayout?.layout_data?.added_fields?.[
                                            layoutRow?.lookup_meta
                                              ?.lookup_keyName ?? ''
                                          ]?.field_name?.toString() ?? ''
                                        }
                                        entityId={entityId}
                                        tabIndex={tabIndexMap?.tabIndexOffset}
                                        isSuggestionAddButton={
                                          layoutRow?.lookup_meta?.is_assign
                                        }
                                      />
                                    )
                                  case ROW_TYPES.SUB_FORM:
                                    return (
                                      <TabSubFormRow
                                        row={layoutRow}
                                        layout={viewLayout}
                                        data={entityData}
                                        tabIndexMap={tabIndexMap}
                                        isRootEntityTab={
                                          mappedAs === VIEW_360_CONSTANTS.ROOT
                                        }
                                        layoutRowType={ROW_TYPES.SUB_FORM}
                                      />
                                    )
                                  default:
                                    return (
                                      <RowRenderTabs
                                        index={index}
                                        row={layoutRow}
                                        layout={viewLayout}
                                        data={entityData || recordId}
                                        editLayout={editLayout}
                                        entityId={entityId}
                                        tabIndexMap={tabIndexMap}
                                        parentLayout={parentLayout}
                                        viewEntityRecordId={
                                          viewEntityRecordId as string
                                        }
                                        parentRow={row}
                                        isRootEntityTab={
                                          mappedAs === VIEW_360_CONSTANTS.ROOT
                                        }
                                      />
                                    )
                                }
                              })()}
                            </div>
                          )
                        }
                      )}
                    </>
                  </HybridFormBehaviorScope>
                </FormProvider>
              )

            case VIEW_360_CONSTANTS.MANYTOMANY:
            case VIEW_360_CONSTANTS.ONETOMANY:
              return (
                <div>
                  <MultiLookupRenderTabs
                    heading={row?.label ?? viewLayout?.name ?? ''}
                    listingLayout={viewLayout}
                    editLayout={editLayout}
                    relationIdentifier={row?.relation_identifier ?? ''}
                    layout={parentLayout}
                    recordId={entityData?.id ?? recordId}
                    entityId={entityId}
                    isAssign={!!row?.lookup_meta?.is_assign}
                    tabIndexMap={tabIndexMap}
                    disableFilters={!!row?.lookup_meta?.disable_filters}
                    lookupKeynameForGlobal={
                      row?.lookup_meta?.lookup_keyName ?? ''
                    }
                    isWorkflow={isWorkflow}
                    // TODO: Need to fix
                    isViewEntity={
                      allEntities[
                        parentLayout?.layout_data?.added_fields?.[
                          row?.keyName ?? ''
                        ]?.relationship?.target_entity ?? ''
                      ]?.type?.toUpperCase() === ENTITY_TYPE.VIEW
                    }
                  />
                </div>
              )

            case ROW_TYPES.NOTE:
            case ROW_TYPES.NOTES:
              return (
                <ThreadsNotesAccordion
                  relationIdentifier={row?.relation_identifier ?? ''}
                  row={row as Row}
                  id={id || '0'}
                  mode={MODE_CONSTANTS.VIEW}
                  createLayoutId={rowMeta?.create_layout_id ?? ''}
                  entityName={rowMeta?.field_name?.toString() ?? ''}
                  entityId={entityId}
                  tabIndex={tabIndexMap?.tabIndexOffset}
                  isSuggestionAddButton={row?.lookup_meta?.is_assign}
                />
              )

            default:
              return null
          }
        })()}
      </div>
    </LayoutRenderTabsProvider>
  )
}

export default LayoutRenderTabs
